import React, { useState } from 'react';
import { func, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import { Button, FieldTextInput, Form, H3, PrimaryButton, TravelStyleField } from '../..';

import css from './CustomBookingForm.module.css';
import { required } from '../../../util/validators';
import { useDispatch, useSelector } from 'react-redux';
import { currentUserIdSelector, userTypeSelector } from '../../../ducks/user.duck';
import { TRAVEL_EXPERT_USER_TYPE } from '../../../util/types';
import IconCollection from '../../IconCollection/IconCollection';
import Modal from '../../Modal/Modal';
import { useHistory } from 'react-router-dom';
import { CLOSE_FINAL_MODAL } from '../../../containers/ListingPage/ListingPage.duck';
import { createResourceLocatorString } from '../../../util/routes';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { manageDisableScrolling } from '../../../ducks/ui.duck';

const DESTINATION_MAX_LENGTH = 100;
const PEOPLE_MAX_LENGTH = 50;
const TRAVEL_DURATION_MAX_LENGTH = 100;
const BUDGET_MAX_LENGTH = 100;
const ADDITIONAL_DETAILS_MAX_LENGTH = 800;

const renderForm = formRenderProps => {
  // FormRenderProps from final-form
  const {
    formId,
    listing,
    className,
    rootClassName,
    handleSubmit,
    form,
    invalid,
    submitInProgress,
    disabled,
  } = formRenderProps;

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const route = useRouteConfiguration();

  const { showFinalModal, requestFormSubmitted } = useSelector(state => state.ListingPage);
  const currentUserId = useSelector(currentUserIdSelector);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const [selectedTravelStyles, setSelectedTravelStyles] = useState([]);

  const { services } = listing?.attributes?.publicData || {};
  const isOwnListing = listing.author.id.uuid === currentUserId;

  const classes = classNames(rootClassName || css.root, className);
  const submitDisabled =
    !isAuthenticated ||
    requestFormSubmitted ||
    invalid ||
    submitInProgress ||
    !selectedTravelStyles.length ||
    disabled || isOwnListing;

  const closeFinaleModal = () => dispatch({ type: CLOSE_FINAL_MODAL });

  const values = form.getState().values;
  return (
    <Form id={formId} onSubmit={handleSubmit} className={classes}>
      <div>
        <Modal
          isOpen={showFinalModal}
          onClose={closeFinaleModal}
          onManageDisableScrolling={disableScrolling =>
            dispatch(manageDisableScrolling('CustomBookingForm', disableScrolling))
          }
        >
          <H3 className={css.finalModalTitle}>
            {intl.formatMessage({ id: 'CustomBookingForm.finalModalMessage' })}
          </H3>
          <p className={css.finalModalSubTitle}>
            {intl.formatMessage({ id: 'CustomBookingForm.finalModalSubMessage' })}
          </p>
          <Button
            onClick={e => {
              e.preventDefault();
              return history.push(createResourceLocatorString('SearchPage', route, {}));
            }}
          >
            {intl.formatMessage({ id: 'CustomBookingForm.finalModalButtonText' })}
          </Button>
        </Modal>
      </div>
      <div className={css.destinationText}>
        <span className={css.boldText}>
          <FormattedMessage id="CustomBookingForm.destination" />
        </span>
        <FormattedMessage id="CustomBookingForm.noProblem" />
      </div>

      <div className={css.textareaInputWrapper}>
        <FieldTextInput
          id={`${formId}destination`}
          name="destination"
          className={css.destinationField}
          type="textarea"
          placeholder={intl.formatMessage({
            id: 'CustomBookingForm.destinationPlaceholder',
          })}
          maxLength={DESTINATION_MAX_LENGTH}
          validate={required(
            intl.formatMessage({
              id: 'CustomBookingForm.required',
            })
          )}
        />
        {/* charactor count */}
        <p className={css.destinationCharCount}>
          {values?.destination?.length || 0}/{DESTINATION_MAX_LENGTH}
        </p>
      </div>

      <div className={css.inputBox}>
        <label>{intl.formatMessage({ id: 'CustomBookingForm.travelStyleLabel' })}</label>
        <TravelStyleField
          selectedTravelStyles={selectedTravelStyles}
          isListingPage
          services={services}
          handleSelection={label => {
            if (selectedTravelStyles.length > 2) return;

            const payload = selectedTravelStyles.includes(label)
              ? selectedTravelStyles
              : [...selectedTravelStyles, label];

            setSelectedTravelStyles(payload);
            form.change('travelStyles', payload);
          }}
        />

        <div className={css.selectedLanguages}>
          {selectedTravelStyles.map(travelStyle => (
            <div key={travelStyle}>
              {travelStyle}
              <span
                onClick={() => {
                  const payload = selectedTravelStyles.filter(elm => elm !== travelStyle);
                  setSelectedTravelStyles(payload);
                  form.change('travelStyles', payload);
                }}
              >
                &nbsp;
                <IconCollection icon="icon-cross-black" />
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={css.inputBox}>
        <FieldTextInput
          id={`${formId}people`}
          name="people"
          className={css.people}
          type="text"
          label={intl.formatMessage({ id: 'CustomBookingForm.peopleLabel' })}
          placeholder={intl.formatMessage({
            id: 'CustomBookingForm.peoplePlaceholder',
          })}
          maxLength={PEOPLE_MAX_LENGTH}
          validate={required(
            intl.formatMessage({
              id: 'CustomBookingForm.required',
            })
          )}
        />
        {/* charactor count */}
        <p className={css.destinationCharCount}>
          {values?.people?.length || 0}/{PEOPLE_MAX_LENGTH}
        </p>
      </div>

      <div className={css.inputBox}>
        <FieldTextInput
          id={`${formId}travelDuration`}
          name="travelDuration"
          className={css.travelDuration}
          type="text"
          label={intl.formatMessage({ id: 'CustomBookingForm.travelDurationLabel' })}
          placeholder={intl.formatMessage({
            id: 'CustomBookingForm.travelDurationPlaceholder',
          })}
          maxLength={TRAVEL_DURATION_MAX_LENGTH}
          validate={required(
            intl.formatMessage({
              id: 'CustomBookingForm.required',
            })
          )}
        />
        {/* charactor count */}
        <p className={css.destinationCharCountThree}>
          {values?.travelDuration?.length || 0}/{TRAVEL_DURATION_MAX_LENGTH}
        </p>
      </div>

      <div className={css.inputBox}>
        <FieldTextInput
          id={`${formId}budget`}
          name="budget"
          className={css.budget}
          type="text"
          label={intl.formatMessage({ id: 'CustomBookingForm.budgetLabel' })}
          placeholder={intl.formatMessage({
            id: 'CustomBookingForm.budgetPlaceholder',
          })}
          maxLength={BUDGET_MAX_LENGTH}
          validate={required(
            intl.formatMessage({
              id: 'CustomBookingForm.required',
            })
          )}
        />
        {/* charactor count */}
        <p className={css.destinationCharCount}>
          {values?.budget?.length || 0}/{BUDGET_MAX_LENGTH}
        </p>
      </div>

      <div className={css.destinationText}>
        <span className={css.boldText}>
          <FormattedMessage id="CustomBookingForm.writeRequest" />
        </span>
        <FormattedMessage id="CustomBookingForm.whatWould" />
      </div>

      <div className={css.textareaInputWrapper}>
        <FieldTextInput
          id={`${formId}additionalDetails`}
          name="additionalDetails"
          className={css.additionalDetails}
          type="textarea"
          placeholder={intl.formatMessage({
            id: 'CustomBookingForm.additionalDetailsPlaceholder',
          })}
          maxLength={ADDITIONAL_DETAILS_MAX_LENGTH}
          validate={required(
            intl.formatMessage({
              id: 'CustomBookingForm.required',
            })
          )}
        />
        {/* charactor count */}
        <p className={css.destinationCharCount}>
          {values?.additionalDetails?.length || 0}/{ADDITIONAL_DETAILS_MAX_LENGTH}
        </p>
      </div>

      <div className={css.submitButton}>
        <PrimaryButton type="submit" disabled={submitDisabled}>
          <FormattedMessage id="CustomBookingForm.ctaButton" />
        </PrimaryButton>
      </div>
    </Form>
  );
};

const CustomBookingForm = props => {
  const intl = useIntl();
  const initialValues = {};

  return <FinalForm initialValues={initialValues} {...props} intl={intl} render={renderForm} />;
};

CustomBookingForm.defaultProps = {
  rootClassName: null,
  className: null,
};

CustomBookingForm.propTypes = {
  rootClassName: string,
  className: string,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,
};

export default CustomBookingForm;
